import { Component, Input } from '@angular/core';
import { LayoutName, LayoutTemplate, SerialData } from 'emma-common-ts/emma';
import { uniq } from 'lodash';

import Chart from 'chart.js';
import { EMMAChartBase } from './emma-chart-base';
import { getConfig, styles } from './emma-coverchart.component.helpers';

declare const mUtil: any;

interface Legend {
  label: string;
  variationValue: number;
  variation: string;
  totalValue: number;
  total: string;
  css: string[];
  color: string;
  icon: string;
}

interface Dataset {
  index: number;
  label: string;
  data: SerialData;
  options?: { [option: string]: any };
}

interface Data {
  index: number;
  label: string;
  data?: SerialData;
  total?: number;
  variation?: number;
  error?: Error;
}

@Component({
    selector: 'emma-coverchart',
    templateUrl: './emma-chart.template.html',
    standalone: false
})
export class EMMACoverChartComponent extends EMMAChartBase implements LayoutTemplate {
  @Input()
  override layout!: LayoutName;

  labels: string[] = [];
  datasets: Dataset[] = [];
  override legends: Legend[] = [];

  override init() {
    const el = document.getElementById(this.chartId) as HTMLCanvasElement;
    if (!el) {
      throw new Error('Chart not found: ' + this.chartId);
    }
    const ctx = el.getContext('2d');
    if (!ctx) {
      throw new Error('Chart without Context2D: ' + this.chartId);
    }
    const config: any = getConfig();
    this.chart = new Chart(ctx, config);
    this.chartLoad.emit(true);
    if (this.export) {
      this.export.subscribe(() => this.exportChart());
    }
  }

  private generateChartDataset = (dataset: Dataset) => {
    const style = styles[dataset.index];
    const graph = {
      label: dataset.label,
      backgroundColor: 'rgba(0,0,0,0)',
      borderColor: mUtil.getColor(style.color),
      pointBackgroundColor: 'rgba(0,0,0,0)',
      pointBorderColor: 'rgba(0,0,0,0)',
      pointHoverBackgroundColor: mUtil.getColor('emmadarkblue'),
      pointHoverBorderColor: 'rgba(0,0,0,0.1)',
      data: this.labels.map((label) => dataset.data.values[label] || 0),
    };
    if (dataset.options && dataset.options.lineOptions) {
      Object.assign(graph, dataset.options.lineOptions);
    }
    return graph;
  };

  protected override _updateChart() {
    if (!this.chart) {
      return;
    }
    this.chart.data.labels = this.labels;
    this.chart.data.datasets = this.datasets.map(this.generateChartDataset);
    this.chart.update();
  }

  addDataset(index: number, label: string, data: SerialData, options: any = {}) {
    // Merge or reset labels
    if (data && data.error) {
      return this.error(data.error);
    } else if (!data || data.loading) {
      return this.loading();
    }
    const labels = this.isLoading ? data.labels.slice(0) : this.labels.concat(data.labels);
    this.loaded();

    if (options.sortableLabels) {
      labels.sort();
    }
    this.labels = uniq(labels);
    this.datasets[index] = { index, label, data, options };
    this._updateChart();
  }

  reset() {
    this.datasets = [];
    this.labels = [];
    this.legends = [];
  }

  override loading() {
    this.reset();
    super.loading();
  }
  override error(error?: Error) {
    this.reset();
    super.error(error);
  }
  override update(_data: Data | null, options?: any) {
    if (_data === null) {
      return this.loading();
    } else if (_data.error) {
      return this.error(_data.error);
    }
    const { index, label, data /* total, variation */ } = _data;
    if (data) {
      this.addDataset(index, label, data, options);
    }
    // if (total !== undefined && variation !== undefined) {
    //   this.addLegend({ index, label, variation, total });
    // }
  }
}
